import axios from 'axios';
import { captureKioskLookupGrant } from './kioskLookupGrant';

// Single-link mode: the backend serves this build, so the API lives at /api on
// the SAME origin that loaded the page. Using a relative base means one build
// works behind any URL/tunnel/domain (no rebuild per URL). Split dev (frontend
// :3000, backend :8001) leaves this unset and uses the absolute backend URL.
const SAME_ORIGIN = process.env.REACT_APP_SAME_ORIGIN === 'true';
const API_URL = SAME_ORIGIN ? '' : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

// The AI (auto-edit) pipeline is reached THROUGH this backend, never directly
// from the browser. It authenticates on self-asserted X-Instructor-Id/X-Role
// headers, so any browser-reachable origin makes an anonymous caller an admin —
// verified against production on 2026-08-03, where an anonymous `GET /jobs`
// returned every customer's name, email and delivery links. So every pipeline
// call now goes to `/api/media/ai-jobs/...` on the main `api` client (which
// carries the SkydiveOS JWT) and is proxied server-side; see the backend's
// `utils/aiBackendProxy.js`.
//
// This constant is the one place that prefix is written down.
export const AI_JOBS_PATH = '/media/ai-jobs';

// When the backend is reached through an ngrok tunnel (test/dev link), ngrok's
// free plan serves a browser-warning interstitial on the first request, which
// returns HTML instead of JSON and breaks every XHR. Sending this header skips
// that page. Only attach it for same-origin (single-link mode, possibly behind
// ngrok) or an explicit ngrok URL — sending it to a normal cross-origin backend
// (e.g. dev-api.ultimatedzm.com) trips CORS preflight because the server's
// Access-Control-Allow-Headers doesn't list this field.
const ngrokHeaderFor = (url, sameOrigin = false) =>
  sameOrigin || /ngrok/i.test(url) ? { 'ngrok-skip-browser-warning': 'true' } : {};

/**
 * Absolute URL of an API path, for the places a browser must NAVIGATE to the
 * API instead of calling it through axios — a file download (BUG 439's gallery
 * ZIP, per-item gallery downloads). Same base the axios instance uses, so
 * single-link mode (`/api` on the page's own origin) and split dev agree.
 * @param {string} path  starts with '/', relative to `/api`
 */
export const apiUrl = (path) => `${API_URL}/api${path}`;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { ...ngrokHeaderFor(API_URL, SAME_ORIGIN) },
});

// ─── Refresh queue state ─────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];
let onRefreshFailure = null;

function processQueue(error, token = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

/**
 * Register a callback that fires when token refresh fails (triggers logout).
 * Called by AuthContext to keep auth state in sync.
 */
export function setRefreshFailureCallback(cb) {
  onRefreshFailure = cb;
}

// ─── Request interceptor — inject access token ──────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// ─── Manifest credential gate — surface response signals from ANY surface ──
// P5b: every /api/manifest/:loadId/jumpers POST may return:
//   - 400 CREDENTIAL_REQUIRED  + missingCredentials[] → block toast
//   - 200 success + credentialWarnings[]              → amber warning toast
// Many call sites (drag-to-load, AddJumper button, group flows, override
// flows) hit this endpoint directly via axios rather than the useAddJumper
// hook, so we tap the interceptor to make the UX consistent everywhere.
// Dedup signature avoids double-toasting if a caller also handles it.
let _recentSignatures = new Map();
const _signature = (sig) => {
  const now = Date.now();
  for (const [k, t] of _recentSignatures) {
    if (now - t > 3000) _recentSignatures.delete(k);
  }
  if (_recentSignatures.has(sig)) return false;
  _recentSignatures.set(sig, now);
  return true;
};

const _isAddJumperRequest = (config) => {
  if (!config) return false;
  const url = String(config.url || '');
  const method = String(config.method || '').toLowerCase();
  // POST /manifest/:loadId/jumpers — add (any flow)
  if (method === 'post' && /\/manifest\/[^/]+\/jumpers(?:\?.*)?$/.test(url)) return true;
  // PUT /manifest/move-jumper — drag from one load to another
  if (method === 'put' && /\/manifest\/move-jumper(?:\?.*)?$/.test(url)) return true;
  return false;
};

const _surfaceManifestSignals = (toastApi) => {
  return {
    onSuccess: (response) => {
      try {
        if (!_isAddJumperRequest(response?.config)) return;
        const warnings = response?.data?.credentialWarnings || [];
        if (warnings.length === 0) return;
        const summary = warnings.map((w) => `${w.label} (${w.status})`).join(', ');
        const sig = `cred-warn:${response.config.url}:${summary}`;
        if (_signature(sig)) {
          toastApi.warning(`Credential warning: ${summary}`, { duration: 8000 });
        }
      } catch { /* noop */ }
    },
    onError: (error) => {
      try {
        if (!_isAddJumperRequest(error?.config)) return;
        if (error?.response?.data?.code !== 'CREDENTIAL_REQUIRED') return;
        const missing = (error.response.data.missingCredentials || [])
          .map((m) => m.label)
          .join(', ');
        const sig = `cred-block:${error.config.url}:${missing}`;
        if (_signature(sig)) {
          toastApi.error(
            `Cannot manifest — missing/expired credential(s): ${missing}. Upload from the customer's Documents or Credentials tab.`,
            { duration: 10000 }
          );
        }
      } catch { /* noop */ }
    },
  };
};

// Lazy import sonner so this module remains importable in non-browser tests.
let _credentialSignalHandler = null;
(async () => {
  try {
    const sonner = await import('sonner');
    _credentialSignalHandler = _surfaceManifestSignals(sonner.toast);
  } catch { /* sonner not available — skip */ }
})();

// ─── Response interceptor — silent token refresh on 401 ─────────────────────
api.interceptors.response.use(
  (response) => {
    _credentialSignalHandler?.onSuccess(response);
    // C6 — a kiosk lookup (or create-booking) hands back a short-lived grant
    // that lets this terminal resolve the bookings it just found. Captured
    // here, once, rather than at each of the four entry points; the header is
    // absent on every other response, so this is a no-op elsewhere.
    captureKioskLookupGrant(response);
    return response;
  },
  async (error) => {
    _credentialSignalHandler?.onError(error);
    const originalRequest = error.config;

    // Only handle 401s that haven't been retried
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If a refresh is already in-flight, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }
    // originalRequest._retry = true;

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token');

      // Use raw axios (not the api instance) to avoid interceptor loop
      const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = data.data;

      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', newRefreshToken);

      processQueue(null, accessToken);
      broadcastTokensUpdated(accessToken, newRefreshToken);

      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('skydiving_user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      broadcastLogout();
      onRefreshFailure?.();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Proactive token refresh ────────────────────────────────────────────────
// Refreshes the access token BEFORE it expires, so 401 cascades never happen.
let proactiveRefreshTimer = null;
const REFRESH_BUFFER_MS = 60 * 1000; // Refresh 60s before expiry
// Backoff for a FAILED proactive refresh. Without it the chain dies on the first
// transient failure (see the catch in doProactiveRefresh) — bounded so a genuinely
// dead session doesn't poll /auth/refresh forever; the reactive 401 path logs out.
const REFRESH_RETRY_MIN_MS = 30 * 1000;
const REFRESH_RETRY_MAX_MS = 5 * 60 * 1000;
let refreshRetryMs = REFRESH_RETRY_MIN_MS;

function getTokenExpiry(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload.exp ? payload.exp * 1000 : null; // Convert to ms
  } catch {
    return null;
  }
}

async function doProactiveRefresh() {
  if (isRefreshing) return; // Reactive refresh already in-flight, skip

  isRefreshing = true;
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return;

    const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = data.data;

    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', newRefreshToken);

    // Resolve any queued requests that may have accumulated
    processQueue(null, accessToken);
    broadcastTokensUpdated(accessToken, newRefreshToken);

    // Schedule the next proactive refresh
    refreshRetryMs = REFRESH_RETRY_MIN_MS;
    scheduleProactiveRefresh(accessToken);
  } catch (err) {
    // Don't logout on proactive failure — let the reactive interceptor handle it.
    // But DO re-arm: this used to just warn, which left the timer chain dead for
    // the rest of the tab's life after a single transient failure. A long upload
    // saturating the uplink is enough to cause one, and the tab then ran on
    // reactive 401s alone — where "retry" means re-sending a multi-GB body.
    console.warn('Proactive token refresh failed, retrying:', err.message);
    if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = setTimeout(doProactiveRefresh, refreshRetryMs);
    refreshRetryMs = Math.min(refreshRetryMs * 2, REFRESH_RETRY_MAX_MS);
  } finally {
    isRefreshing = false;
  }
}

/**
 * Make sure the access token has real life left, and wait if it doesn't.
 *
 * For a request that takes MINUTES (an AI Auto-Edit footage upload), "the token
 * was valid when we started" is not enough: the direct-to-S3 flow authenticates
 * `upload-urls`, spends ten minutes PUTting to S3, then authenticates
 * `attach-s3` — with a token minted before any of it. A 15-minute access token
 * loses that race routinely, and the reactive 401 retry pays for it by re-sending
 * the whole body.
 *
 * Best-effort ON PURPOSE: a refresh that fails resolves anyway, so this can never
 * be the reason an upload doesn't start. The reactive interceptor is still behind
 * it. Reuses the shared `isRefreshing` queue, so it never runs a second refresh
 * alongside one already in flight.
 */
export async function ensureFreshToken(minRemainingMs = 5 * 60 * 1000) {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  const expiry = getTokenExpiry(token);
  if (expiry && expiry - Date.now() > minRemainingMs) return token;

  if (isRefreshing) {
    // Join the in-flight refresh rather than starting a competing one. A failure
    // resolves null instead of rejecting — the caller proceeds either way.
    return new Promise((resolve) => {
      failedQueue.push({ resolve, reject: () => resolve(null) });
    });
  }

  await doProactiveRefresh();
  return localStorage.getItem('access_token');
}

function scheduleProactiveRefresh(accessToken) {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);

  const expiry = getTokenExpiry(accessToken);
  if (!expiry) return;

  const msUntilRefresh = expiry - Date.now() - REFRESH_BUFFER_MS;

  if (msUntilRefresh <= 0) {
    // Token already expired or about to — refresh immediately
    doProactiveRefresh();
    return;
  }

  proactiveRefreshTimer = setTimeout(doProactiveRefresh, msUntilRefresh);
}

export function startProactiveRefresh() {
  const token = localStorage.getItem('access_token');
  if (token) scheduleProactiveRefresh(token);
}

export function stopProactiveRefresh() {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

// ─── Cross-tab coordination ─────────────────────────────────────────────────
// Syncs token refreshes and logouts across browser tabs.
let authChannel = null;

try {
  authChannel = new BroadcastChannel('skydiving-auth');
  authChannel.onmessage = (event) => {
    const { type, accessToken, refreshToken } = event.data;

    if (type === 'tokens-updated' && accessToken && refreshToken) {
      // Another tab refreshed — pick up the new tokens
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);

      // If this tab was also trying to refresh, resolve its queue
      if (isRefreshing) {
        processQueue(null, accessToken);
        isRefreshing = false;
      }

      // Restart proactive refresh with the new token's expiry
      scheduleProactiveRefresh(accessToken);
    }

    if (type === 'logout') {
      // Another tab logged out — sync this tab
      stopProactiveRefresh();
      localStorage.removeItem('skydiving_user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      onRefreshFailure?.();
    }
  };
} catch {
  // BroadcastChannel not available — fall back to storage events
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token' && event.newValue) {
      // Another tab updated the token
      if (isRefreshing) {
        processQueue(null, event.newValue);
        isRefreshing = false;
      }
      scheduleProactiveRefresh(event.newValue);
    }

    if (event.key === 'access_token' && !event.newValue) {
      // Another tab cleared tokens (logout)
      stopProactiveRefresh();
      onRefreshFailure?.();
    }
  });
}

export function broadcastTokensUpdated(accessToken, refreshToken) {
  authChannel?.postMessage({ type: 'tokens-updated', accessToken, refreshToken });
}

export function broadcastLogout() {
  authChannel?.postMessage({ type: 'logout' });
}

export default api;
